// Render-time gameplay events (audio §3, ui-qol §3/§9.1): turns `store.view.eatEvents` and the
// own draw list of each `store.sample(rt)` into client-bus events, so sounds and UI cues fire
// when the change is drawn, not when its packet arrives.
//
// app.ts calls `tap.afterSample(store)` once per display frame, right after `store.sample(rt)`.
// For each eat event, in order:
//   eat (always) → then, by kind:
//   - eaten own cell, eater own cell            → ownMerge
//   - eaten own cell, ownLeft = 0               → ownLastCellEaten
//   - eaten own cell, eaten by another player   → ownCellEaten
//   - eaten virus                               → virusPopped
// And once per life: firstOwnCellDrawn, on the first frame a cell of the current life is drawn.
//
// The payloads are reused objects (no allocation per frame): read them during the call.
//
// Implemented fully (contracts step). DOM-free. Consumer: app.ts.

import { KIND } from '@ballio/shared';
import type { WorldStore } from '../game/world-store.js';
import type { ClientBus, ClientEvents } from './bus.js';

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

export class RenderEventTap {
  private readonly bus: ClientBus;
  /** The life whose first drawn cell was announced (0 = none yet). */
  private announcedLife = 0;
  private readonly merge: Mutable<ClientEvents['ownMerge']> = { survivorId: 0, mergedId: 0 };
  private readonly pop: Mutable<ClientEvents['virusPopped']> = {
    virusId: 0,
    eaterId: 0,
    eaterPlayerId: 0,
    own: false,
  };
  private readonly lost: Mutable<ClientEvents['ownCellEaten']> = {
    cellId: 0,
    eaterPlayerId: 0,
    ownLeft: 0,
  };
  private readonly last: Mutable<ClientEvents['ownLastCellEaten']> = {
    cellId: 0,
    eaterPlayerId: 0,
  };
  private readonly first: Mutable<ClientEvents['firstOwnCellDrawn']> = { playerId: 0 };

  constructor(bus: ClientBus) {
    this.bus = bus;
  }

  /** Emits the events of the last `store.sample()`; call once per sample. */
  afterSample(store: WorldStore): void {
    const bus = this.bus;
    const view = store.view;
    const events = view.eatEvents;
    for (let i = 0; i < events.length; i++) {
      const ev = events[i];
      if (!ev) continue;
      bus.emit('eat', ev);
      if (ev.eatenKind === KIND.CELL && ev.eatenOwn) {
        if (ev.eaterOwn) {
          this.merge.survivorId = ev.eaterId;
          this.merge.mergedId = ev.eatenId;
          bus.emit('ownMerge', this.merge);
        } else if (ev.ownLeft === 0) {
          this.last.cellId = ev.eatenId;
          this.last.eaterPlayerId = ev.eaterPlayerId;
          bus.emit('ownLastCellEaten', this.last);
        } else {
          this.lost.cellId = ev.eatenId;
          this.lost.eaterPlayerId = ev.eaterPlayerId;
          this.lost.ownLeft = ev.ownLeft;
          bus.emit('ownCellEaten', this.lost);
        }
      } else if (ev.eatenKind === KIND.VIRUS) {
        this.pop.virusId = ev.eatenId;
        this.pop.eaterId = ev.eaterId;
        this.pop.eaterPlayerId = ev.eaterPlayerId;
        this.pop.own = ev.eaterOwn;
        bus.emit('virusPopped', this.pop);
      }
    }

    const life = store.own.playerId;
    if (life !== 0 && life !== this.announcedLife) {
      const own = view.own;
      for (let i = 0; i < own.length; i++) {
        if (own[i]?.playerId === life) {
          this.announcedLife = life;
          this.first.playerId = life;
          bus.emit('firstOwnCellDrawn', this.first);
          break;
        }
      }
    }
  }

  /** New socket: forget the announced life. */
  reset(): void {
    this.announcedLife = 0;
  }
}
